import { MIN_FENCE_SIZE_PX } from "../constants/damage-annotation.constants";

export type Point = {
	x: number;
	y: number;
};

export type Size = {
	width: number;
	height: number;
};

export type ImageRect = Point & Size;

export type ContainTransform = {
	scale: number;
	offsetX: number;
	offsetY: number;
	drawW: number;
	drawH: number;
};

export function getContainTransform(
	container: Size,
	image: Size
): ContainTransform | null {
	if (
		container.width <= 0 ||
		container.height <= 0 ||
		image.width <= 0 ||
		image.height <= 0
	) {
		return null;
	}

	const imageRatio = image.width / image.height;
	const containerRatio = container.width / container.height;

	if (imageRatio > containerRatio) {
		const drawW = container.width;
		const drawH = container.width / imageRatio;
		return {
			scale: drawW / image.width,
			offsetX: 0,
			offsetY: (container.height - drawH) / 2,
			drawW,
			drawH,
		};
	}

	const drawH = container.height;
	const drawW = container.height * imageRatio;
	return {
		scale: drawW / image.width,
		offsetX: (container.width - drawW) / 2,
		offsetY: 0,
		drawW,
		drawH,
	};
}

export function clampScreenPointToVisibleImageContain(
	point: Point,
	container: Size,
	image: Size
): Point | null {
	const transform = getContainTransform(container, image);
	if (!transform) {
		return null;
	}

	return {
		x: Math.max(
			transform.offsetX,
			Math.min(point.x, transform.offsetX + transform.drawW)
		),
		y: Math.max(
			transform.offsetY,
			Math.min(point.y, transform.offsetY + transform.drawH)
		),
	};
}

export function screenToImageCoordsContain(
	point: Point,
	container: Size,
	image: Size
): Point | null {
	const transform = getContainTransform(container, image);
	if (!transform) {
		return null;
	}

	const x = (point.x - transform.offsetX) / transform.scale;
	const y = (point.y - transform.offsetY) / transform.scale;

	if (x < 0 || y < 0 || x > image.width || y > image.height) {
		return null;
	}

	return { x, y };
}

export function imageRectToScreenContain(
	rect: ImageRect,
	container: Size,
	image: Size
): ImageRect | null {
	const transform = getContainTransform(container, image);
	if (!transform) {
		return null;
	}

	return {
		x: rect.x * transform.scale + transform.offsetX,
		y: rect.y * transform.scale + transform.offsetY,
		width: rect.width * transform.scale,
		height: rect.height * transform.scale,
	};
}

export function normalizeRectInBounds(
	start: Point,
	end: Point,
	maxSize: Size,
	minSize: number
): ImageRect | null {
	const x = Math.max(0, Math.min(start.x, end.x));
	const y = Math.max(0, Math.min(start.y, end.y));
	const right = Math.min(maxSize.width, Math.max(start.x, end.x));
	const bottom = Math.min(maxSize.height, Math.max(start.y, end.y));
	const width = right - x;
	const height = bottom - y;

	if (width < minSize || height < minSize) {
		return null;
	}

	return { x, y, width, height };
}

export function screenRectToImageRectContain(
	start: Point,
	end: Point,
	container: Size,
	image: Size
): ImageRect | null {
	const clampedStart = clampScreenPointToVisibleImageContain(
		start,
		container,
		image
	);
	const clampedEnd = clampScreenPointToVisibleImageContain(
		end,
		container,
		image
	);
	if (!(clampedStart && clampedEnd)) {
		return null;
	}

	const imageStart = screenToImageCoordsContain(clampedStart, container, image);
	const imageEnd = screenToImageCoordsContain(clampedEnd, container, image);
	if (!(imageStart && imageEnd)) {
		return null;
	}

	const transform = getContainTransform(container, image);
	const minImageSize = transform
		? MIN_FENCE_SIZE_PX / transform.scale
		: MIN_FENCE_SIZE_PX;

	return normalizeRectInBounds(imageStart, imageEnd, image, minImageSize);
}

export function pointInImageRect(point: Point, rect: ImageRect): boolean {
	return (
		point.x >= rect.x &&
		point.x <= rect.x + rect.width &&
		point.y >= rect.y &&
		point.y <= rect.y + rect.height
	);
}

export function computeExportDimensions(
	image: Size,
	maxLongEdge: number
): Size {
	if (image.width <= 0 || image.height <= 0) {
		return image;
	}

	const longEdge = Math.max(image.width, image.height);
	const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;

	return {
		width: Math.round(image.width * scale),
		height: Math.round(image.height * scale),
	};
}

export function normalizeScreenDraft(start: Point, end: Point): ImageRect {
	return {
		x: Math.min(start.x, end.x),
		y: Math.min(start.y, end.y),
		width: Math.abs(end.x - start.x),
		height: Math.abs(end.y - start.y),
	};
}
